import { Cart } from "@dana/Overrides/Services/CartService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import setOrderNotes from "@dana/Store/Pages/OnePageCheckoutExt/Handlers/SetOrderNotes";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartMode = getCookie('cartMode');
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartMode === 'StockOrderCart'
        ? cartState.value
        : cartMode === "EmergencyOrderCart"
            ? state.emergencyCartState.cart as Cart ?? { properties: {} } as Cart
            : state.annualReturnsCartState.cart as Cart ?? { properties: {} } as Cart;
    return {
        cartMode,
        cart,
    };
};

const mapDispatchToProps = {
    setOrderNotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDispositionDecisionStyles {
    container?: GridContainerProps;
    headingContainer?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    servicesGridContainer?: GridContainerProps;
    servicesGridItem?: GridItemProps;
}

export const onePageCheckoutDispositionDecisionStyles: OnePageCheckoutDispositionDecisionStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 120px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
};

const styles = onePageCheckoutDispositionDecisionStyles;

const OnePageCheckoutDispositionDecision = ({
    cartMode,
    cart,
    setOrderNotes,
}: Props) => {
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState("");
    const [errorDecisionMsg, setErrorDecisionMsg] = useState("");

    useEffect(() => {
        if (!selectedDecision) {
            setErrorDecisionMsg(translate("Please indicate your disposition preference"));
        } else {
            setErrorDecisionMsg("");
        }
    }, [selectedDecision]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_dispositionDecisionLoading" />
            </StyledWrapper>
        );
    }

    const decisionChangeHandler = (decision: string) => {
        setSelectedDecision(decision);
        setOrderNotes({ orderNotes: decision, });
    };

    if (cartMode !== "AnnualReturnsOrderCart") {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridContainer gap={0} {...styles.headingContainer}>
                <GridItem {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {translate("Disposition Decision")}
                    </Typography>
                </GridItem>
            </GridContainer>
            <GridItem {...styles.fulfillmentMethodGridItem}>
                <RadioGroup
                    name="dispositionDecisionRadioGroup"
                    required
                    error={errorDecisionMsg}
                    label={translate("If any of these parts are deemed non-returnable for credit (not on the RMA, damaged, rusted, nicks, etc.), please indicate your disposition preference:")}
                    onChangeHandler={event => decisionChangeHandler(event.currentTarget.value)}
                    {...styles.servicesRadioGroup}
                    data-test-selector="dispositionDecision"
                    value={selectedDecision}
                >
                    <Radio
                        value="RETURN_FREIGHT_COLLECT_TO_YOUR_LOCATION"
                        {...styles.serviceRadio}
                        data-test-selector="disposition_return"
                    >
                        {translate("Return freight collect to your location")}
                    </Radio>
                    <Radio
                        value="SCRAP_AT_DANA_LOCATION"
                        {...styles.serviceRadio}
                        data-test-selector="disposition_scrap"
                    >
                        {translate("Scrap at our location")}
                    </Radio>
                </RadioGroup>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDispositionDecision),
    definition: {
        displayName: "Disposition Decision for Annual Returns",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
